import React, { useState } from 'react';
import { Card, Col, DatePicker, Input, InputNumber, Row, Select, Spin } from 'antd';
import moment from 'moment';
import { RefundRequest } from '../../pages/RefundRequests';
import callApi from '../../Api/config';
import { toastError, toastSuccess } from '../../service/Toast';
import { REFUND_RECEIVED_IN_OPTIONS } from '../../constants/paymentVoucher';
import { InvertFillButton } from '../InvertFillButton';

interface RefundPaymentReconciliationProps {
  refund: RefundRequest;
  /** Accountant / Super Admin. */
  canReconcile: boolean;
  /** Super Admin may skip this step; it is mandatory for everyone else. */
  isSuperAdmin: boolean;
  titleStyle: React.CSSProperties;
  onSaved: (patch: Partial<RefundRequest>) => void;
}

const formatDate = (value?: string | null) => (value ? moment(value).format('DD/MM/YYYY') : '--');

/**
 * Accountant Payment Reconciliation — Application Charges refunds, available
 * while the refund is pending or approved (independent of approval). Internal only.
 */
const RefundPaymentReconciliation: React.FC<RefundPaymentReconciliationProps> = ({
  refund,
  canReconcile,
  isSuperAdmin,
  titleStyle,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const [suspenseVoucherId, setSuspenseVoucherId] = useState('');
  const [receivedAmount, setReceivedAmount] = useState<number | null>(null);
  const [receivedDate, setReceivedDate] = useState<moment.Moment | null>(moment());
  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedIn, setReceivedIn] = useState<string | undefined>();

  const refundId = refund.id || refund.refundRequestId;
  // Flag instead of silently clamping on blur.
  const receivedAmountError =
    receivedAmount != null && receivedAmount <= 0 ? 'Amount must be greater than 0' : null;
  const isReconciled = Boolean(refund.reconciliationAt);

  const handleSave = async () => {
    if (receivedAmount == null || receivedAmount <= 0) {
      toastError('Please enter a valid received amount');
      return;
    }
    if (!receivedDate) {
      toastError('Please select the received date');
      return;
    }
    if (!receivedFrom.trim() || !receivedIn) {
      toastError('Please fill Received From and Received In');
      return;
    }

    try {
      setSaving(true);
      const response: any = await callApi(`/v1/refund/status/${refundId}/reconciliation`, 'PUT', {
        suspenseVoucherId: suspenseVoucherId.trim() || undefined,
        receivedAmount,
        receivedDate: receivedDate.format('YYYY-MM-DD'),
        receivedFrom: receivedFrom.trim(),
        receivedIn,
      });

      if (response?.data?.isSuccess) {
        const saved = response.data.data || {};
        toastSuccess('Payment reconciliation saved');
        onSaved({
          reconciliationSuspenseVoucherId:
            saved.reconciliationSuspenseVoucherId ?? (suspenseVoucherId.trim() || null),
          reconciliationReceivedAmount:
            saved.reconciliationReceivedAmount != null
              ? Number(saved.reconciliationReceivedAmount)
              : receivedAmount,
          reconciliationReceivedDate:
            saved.reconciliationReceivedDate || receivedDate.format('YYYY-MM-DD'),
          reconciliationReceivedFrom: saved.reconciliationReceivedFrom || receivedFrom.trim(),
          reconciliationReceivedIn: saved.reconciliationReceivedIn || receivedIn,
          reconciliationBy: saved.reconciliationBy || null,
          reconciliationAt: saved.reconciliationAt || new Date().toISOString(),
        });
      } else {
        toastError(response?.data?.message || 'Failed to save payment reconciliation');
      }
    } catch (error) {
      console.error('Error saving payment reconciliation:', error);
      toastError('An error occurred while saving payment reconciliation');
    } finally {
      setSaving(false);
    }
  };

  const renderReadOnly = () => (
    <Row gutter={[48, 24]}>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Suspense Voucher ID</div>
          <div className="detail-value">{refund.reconciliationSuspenseVoucherId || '--'}</div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Received Amount</div>
          <div className="detail-value amount-approved">
            ${Number(refund.reconciliationReceivedAmount ?? 0).toFixed(2)} USD
          </div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Received Date</div>
          <div className="detail-value">{formatDate(refund.reconciliationReceivedDate)}</div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Received From</div>
          <div className="detail-value">{refund.reconciliationReceivedFrom || '--'}</div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Received In</div>
          <div className="detail-value">{refund.reconciliationReceivedIn || '--'}</div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Reconciled By</div>
          <div className="detail-value">{refund.reconciliationBy || '--'}</div>
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="detail-row">
          <div className="detail-label">Reconciled On</div>
          <div className="detail-value">{formatDate(refund.reconciliationAt)}</div>
        </div>
      </Col>
    </Row>
  );

  const renderForm = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} lg={6}>
        <div className="status-field">
          <div>Suspense Voucher ID (Optional)</div>
          <Input
            className="content-rounded-field"
            value={suspenseVoucherId}
            onChange={event => setSuspenseVoucherId(event.target.value)}
            placeholder="Suspense voucher ID"
          />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="status-field">
          <div className="required-field">Received Amount (USD)</div>
          <InputNumber
            className="content-rounded-field"
            style={{ width: '100%' }}
            status={receivedAmountError ? 'error' : undefined}
            precision={2}
            value={receivedAmount ?? undefined}
            onChange={value => setReceivedAmount(value)}
          />
          {receivedAmountError && <div className="amount-error">{receivedAmountError}</div>}
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="status-field">
          <div className="required-field">Received Date</div>
          <DatePicker
            className="content-rounded-field"
            style={{ width: '100%' }}
            value={receivedDate}
            onChange={value => setReceivedDate(value)}
            disabledDate={current => current && current > moment().endOf('day')}
            format="YYYY-MM-DD"
          />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="status-field">
          <div className="required-field">Received From</div>
          <Input
            className="content-rounded-field"
            value={receivedFrom}
            onChange={event => setReceivedFrom(event.target.value)}
            placeholder="e.g. Government / Embassy"
          />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <div className="status-field">
          <div className="required-field">Received In</div>
          <Select
            className="content-rounded-field"
            style={{ width: '100%' }}
            value={receivedIn}
            onChange={value => setReceivedIn(value)}
            options={REFUND_RECEIVED_IN_OPTIONS.map(option => ({ value: option, label: option }))}
            placeholder="Select account"
          />
        </div>
      </Col>
      <Col span={24}>
        <InvertFillButton variant="blue" onClick={handleSave}>
          Save Reconciliation
        </InvertFillButton>
      </Col>
    </Row>
  );

  const renderBody = () => {
    if (isReconciled) return renderReadOnly();
    // Accountant / Super Admin can reconcile independently of approval.
    const isOpenForReconciliation = refund.status === 'PENDING' || refund.status === 'APPROVED';
    if (!isOpenForReconciliation) return null;
    if (!canReconcile) {
      return (
        <div className="awaiting-step-note">
          Awaiting payment reconciliation by Accountant / Super Admin.
        </div>
      );
    }
    return renderForm();
  };

  const body = renderBody();
  if (!body) return null;

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={titleStyle}>
            Accountant Payment Reconciliation{isSuperAdmin && !isReconciled ? ' (Optional)' : ''}
          </span>
          <span className="internal-only-tag">Internal only</span>
        </div>
      }
      style={{ marginBottom: 16, borderRadius: 8 }}
      bordered
    >
      <Spin spinning={saving}>{body}</Spin>
    </Card>
  );
};

export default RefundPaymentReconciliation;
